import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { camelCase } from 'camel-case';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import { Link } from 'gatsby';
import axios from 'axios';

// import Themes
import { themeDefault, themeDefaultDark } from '../../theme/themeDefault';
import { themeTest, themeTestDark } from '../../theme/themeTest';
import { corner, cornerDark } from '../../theme/themeCorner';
import { jesse, jesseDark } from '../../theme/themeJesse';
import { scorcher, scorcherDark } from '../../theme/themeScorcher';

import RouterTracker from '../../components/RouterTracker';
import ButtonOutline from '../../components/ButtonOutline';

import { generateTheme } from '../../theme/utils/generateTheme';
import ThemeContext from '../../context/ThemeContext';
import JSONPretty from 'react-json-pretty';

var tinycolor = require('tinycolor2');

const SettingsStyles = styled.section(
  (props) => `
  padding: ${props.theme.size.base * 13}px 
    ${props.theme.size.base * 4}px 
    ${props.theme.size.base * 4}px;
  ${
    (props.breakpoints.lg || props.breakpoints.xl) &&
    `
   padding: ${props.theme.size.base * 8}px;
  `
  };
  .body {
    position: relative;
    z-index: 500;
    max-width: ${props.theme.size.base * 128}px;
    margin: 0 auto;
  }
  .code {
    overflow: auto;
    padding: ${props.theme.size.base * 4}px;
    background: ${props.theme.color.background[15]};
    &::-webkit-scrollbar {
      width: 4px;
      height: 4px;
    }
    
    &::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0);
    }
    
    &::-webkit-scrollbar-thumb {
      background-color: rgba(255, 255, 255, 0.4);
      background: rgba(255, 255, 255, 0.4);
      background: ${props.theme.color.secondary[40]};
      background: linear-gradient(
        30deg,
        ${props.theme.color.secondary[35]},
        ${props.theme.color.tertiary[50]}
      );
      border: 0;
    }
  }
  `
);
const Panel = styled.div(
  (props) => `
  padding-bottom: ${props.theme.size.base * 4}px;
  margin-bottom: ${props.theme.size.base * 4}px;
  border-bottom: 1px solid ${tinycolor(props.theme.color.primary[50])
    .setAlpha(0.3)
    .toString()};
`
);
const PanelTitle = styled.div(
  (props) => `
  margin-bottom: ${props.theme.size.base * 4}px;
  .typesetter & {
    h1, h2, h3, h4, h5 {
      margin: 0;
      color: ${props.theme.color.foreground[100]};
      font-size: ${props.theme.size.fontSize[3]};
      line-height: ${props.theme.size.lineHeight[3]};
    }
    p {
      margin: 0;
      color: ${props.theme.color.foreground[0]};
      font-size: ${props.theme.size.fontSize[2]};
      line-height: ${props.theme.size.lineHeight[2]};
    }
  }
`
);
const ControlSet = styled.div(
  (props) => `
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  & > * {
    margin-right: ${props.theme.size.base * 2}px;
  }
  `
);
const ThemeSet = styled.div(
  (props) => `
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin: ${props.theme.size.base * -2}px;
  `
);
const CustomThemeForm = styled.div(
  (props) => `
  display: flex;
  .form-image  {
    width: ${props.theme.size.base * 22}px;
  }
  .form-input {
    padding: ${props.theme.size.base * 4}px;
  }
`
);
const ThemeSwatch = styled.div(
  (props) => `
  flex: 1;
  position: relative;
  cursor: pointer;
  &.list-item {
    min-width: 50%; 
    max-width: 50%; 
    ${
      props.breakpoints.sm &&
      `
      min-width: 33.33%; 
      max-width: 33.33%; 
    `
    };
    ${
      props.breakpoints.md &&
      `
      min-width: 25%; 
      max-width: 25%; 
    `
    };
    ${
      (props.breakpoints.lg || props.breakpoints.xl) &&
      `
      min-width: 20%; 
      max-width: 20%; 
    `
    };
  }
  .inner {
    padding: ${props.theme.size.base * 2}px;
  }
  .name {
    text-align: center;
    color: ${props.theme.color.foreground[50]};
    transition: color 450ms ease;
    font-size: ${props.theme.size.fontSize[4]};
    line-height: ${props.theme.size.lineHeight[4]};
    margin-bottom: ${props.theme.size.base * 0.75}px;
    font-weight: 300;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &:hover {
    .name {
      color: ${props.theme.color.primary[70]};
    }
  }
  .image {
    width: 100%;
    height: 0;
    padding-bottom: 100%;
    position: relative;
    overflow: hidden;
    border-radius: ${props.theme.size.base}px;
    img {
      position: absolute;
      height: 100%;
      object-fit: cover;
    }
  }
  .active {
    position: absolute;
    pointer-events: none;
    top: ${props.theme.size.base - 2}px;
    right: ${props.theme.size.base - 2}px;
    bottom: ${props.theme.size.base - 2}px;
    left: ${props.theme.size.base - 2}px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid ${props.theme.color.primary[50]};
    border-radius: ${props.theme.size.base}px;
    transition: border-color 450ms ease;
    box-shadow: 0 4px 16px
        ${
          props.theme.dark
            ? tinycolor(props.theme.color.primary[25]).setAlpha.toString()
            : tinycolor(props.theme.color.primary[75]).setAlpha.toString()
        },
      inset 0 0 16px
        ${
          props.theme.dark
            ? tinycolor(props.theme.color.primary[25]).setAlpha.toString()
            : tinycolor(props.theme.color.primary[75]).setAlpha.toString()
        };
  }
  .swatch-set {
    z-index: 999;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: ${props.theme.size.base * 2}px;
    display: flex;
    flex-direction: row;
    margin-bottom: ${props.theme.size.base}px;
    overflow: hidden;
    transition: height 450ms ease;
  }
  &:hover .swatch-set {
    height: ${props.theme.size.base * 23}px;
  }
  .swatches {
    flex: 1 0 20%;
    display: flex;
    flex-direction: row;
    .swatch {
      flex: 1 0 100%;
      height: 100%;
      box-shadow: 0 ${props.theme.size.base * 0.5}px
        ${props.theme.size.base * 2}px
        ${props.theme.color.background[10]};
    }
  `
);

const Settings = (props) => {
  const [availableThemes, setAvailableThemes] = useState([
    { light: themeDefault, dark: themeDefaultDark },
    { light: corner, dark: cornerDark },
    { light: scorcher, dark: scorcherDark },
  ]);
  const [albumTheme, setAlbumTheme] = useState([]);
  const [test, setTest] = useState({
    name: 'Under Krameria',
    image:
      'https://is1-ssl.mzstatic.com/image/thumb/Music124/v4/79/a2/d6/79a2d61e-6136-0dc3-c909-c9762a8fbd46/artwork.jpg/170x170bb.jpg',
  });

  const setColors = async (img) => {
    try {
      const colors = await axios.get(
        `https://kuaa-api-7xgcl.ondigitalocean.app/generate-theme?img=${img}`
      );
      const albumtheme = generateTheme(colors.data, img, test.name);
      setAlbumTheme(albumtheme); // set State
    } catch (err) {
      console.error(err.message);
    }
  };

  const breakpoints = useBreakpoint();

  // On Posts update
  useEffect(() => {
    setColors(test.image);
  }, [test]);

  const handleNameChange = (e) => {
    setTest({ ...test, name: e.target.value });
  };
  const handleImageChange = (e) => {
    setTest({ ...test, image: e.target.value });
  };

  return (
    <SettingsStyles theme={props.theme} breakpoints={breakpoints}>
      <RouterTracker />
      <div className='body typesetter'>
        <Panel theme={props.theme}>
          <h1>Settings</h1>
          <p>
            These are experimental settings and may produce undesired results.
            This <Link to='/test'>Test Page</Link> is available to test these
            settings.
          </p>
        </Panel>
        <Panel theme={props.theme}>
          <PanelTitle theme={props.theme}>
            <h3>Brightness</h3>
          </PanelTitle>
          <ControlSet theme={props.theme}>
            <ButtonOutline
              active={props.theme.dark}
              onClick={() => props.theme.setDarkMode(true)}
              label='Enable Dark Mode'
            >
              ☾ Dark
            </ButtonOutline>
            <ButtonOutline
              active={!props.theme.dark}
              onClick={() => props.theme.setDarkMode(false)}
              label='Enable Light Mode'
            >
              ☀ Light
            </ButtonOutline>
          </ControlSet>
        </Panel>

        <Panel theme={props.theme}>
          <PanelTitle theme={props.theme}>
            <h3>Color Sync</h3>
            <p>Sync the app colors with the current track's album art.</p>
          </PanelTitle>
          <ControlSet theme={props.theme}>
            <ButtonOutline
              active={props.theme.sync}
              onClick={() => props.theme.setColorSync(true)}
            >
              ⟳ Sync Color
            </ButtonOutline>
            <ButtonOutline
              active={!props.theme.sync}
              onClick={() => props.theme.setColorSync(false)}
            >
              Use Theme
            </ButtonOutline>
          </ControlSet>
        </Panel>
        <Panel theme={props.theme}>
          <PanelTitle theme={props.theme}>
            <h3>Theme</h3>
          </PanelTitle>
          <ThemeSet theme={props.theme}>
            {availableThemes &&
              availableThemes.map((availableTheme, index) => (
                <ThemeSwatch
                  breakpoints={breakpoints}
                  theme={props.theme}
                  key={index}
                  className='list-item'
                  onClick={() =>
                    props.theme.setTheme(
                      availableTheme.light,
                      availableTheme.dark,
                      props.theme.dark
                    )
                  }
                >
                  <div className='inner'>
                    <div className='name'>
                      {availableTheme.light.themeName}

                      {props.theme.preferredTheme.light?.themeName ===
                        availableTheme.light.themeName && (
                        <div className='active'></div>
                      )}
                    </div>

                    <div className='image'>
                      <div className='swatch-set' key={`color-${index}`}>
                        {Object.values(
                          availableTheme[props.theme.dark ? 'dark' : 'light']
                            .color
                        ).map(
                          (color, index) =>
                            index < 4 && (
                              <div
                                className='swatches'
                                key={`color-set-${index}`}
                              >
                                {color.map((swatch, index2) => (
                                  <div
                                    key={`color-set-${index}-${index2}`}
                                    className='swatch'
                                    style={{
                                      background: swatch,
                                      color: tinycolor
                                        .mostReadable(swatch, [
                                          props.theme.color.background[0],
                                          props.theme.color.foreground[100],
                                        ])
                                        .toHexString(),
                                    }}
                                  ></div>
                                ))}
                              </div>
                            )
                        )}
                      </div>
                      <img
                        src={availableTheme.light.themeImage}
                        alt={availableTheme.light.themeName}
                      />
                    </div>
                  </div>
                </ThemeSwatch>
              ))}
          </ThemeSet>
        </Panel>
        <Panel theme={props.theme}>
          <PanelTitle theme={props.theme}>
            <h3>Custom Theme</h3>
            <p>
              This is an experimental feature for generating new Themes based on
              an iamge.
            </p>
          </PanelTitle>
          <CustomThemeForm theme={props.theme}>
            <div className='form-image'>
              <ThemeSet theme={props.theme}>
                {albumTheme.themeLight && (
                  <ThemeSwatch
                    breakpoints={breakpoints}
                    theme={props.theme}
                    onClick={() =>
                      props.theme.setTheme(
                        albumTheme.themeLight,
                        albumTheme.themeDark,
                        props.theme.dark
                      )
                    }
                  >
                    <div className='inner'>
                      <div className='name'>
                        {albumTheme.themeLight.themeName}
                        {props.theme.preferredTheme.light &&
                          props.theme.preferredTheme.light.themeName ===
                            albumTheme.themeLight.themeName && (
                            <div className='active'></div>
                          )}
                      </div>
                      <div className='image'>
                        <div className='swatch-set'>
                          {Object.values(
                            Object.values(albumTheme)[props.theme.dark ? 1 : 0]
                              .color
                          ).map(
                            (color, index) =>
                              index < 4 && (
                                <div
                                  className='swatches'
                                  key={`color-set-${index}`}
                                >
                                  {color.map((swatch, index2) => (
                                    <div
                                      className='swatch'
                                      key={`color-set-${index}-${index2}`}
                                      style={{
                                        background: swatch,
                                        color: tinycolor
                                          .mostReadable(swatch, [
                                            props.theme.color.background[0],
                                            props.theme.color.foreground[100],
                                          ])
                                          .toHexString(),
                                      }}
                                    ></div>
                                  ))}
                                </div>
                              )
                          )}
                        </div>

                        <img
                          src={albumTheme.themeLight.themeImage}
                          alt={albumTheme.themeLight.themeName}
                        />
                      </div>
                    </div>
                  </ThemeSwatch>
                )}
              </ThemeSet>
            </div>
            <div className='form-input'>
              <label htmlFor='themeName'>Name</label>
              <input
                type='text'
                name='themeName'
                id='themeName'
                value={test.name}
                onChange={handleNameChange}
                style={{
                  width: 'calc(100% - 10px)',
                  padding: '4px',
                  marginBottom: '8px',
                }}
              />
              <label htmlFor='themeImage'>Image</label>
              <input
                type='text'
                name='themeImage'
                id='themeImage'
                value={test.image}
                onChange={handleImageChange}
                style={{
                  width: 'calc(100% - 10px)',
                  padding: '4px',
                  marginBottom: '8px',
                }}
              />
            </div>
          </CustomThemeForm>
          {Object.values(albumTheme)[0] && (
            <pre className='code' theme={props.theme}>
              {`export const ${camelCase(
                Object.values(albumTheme)[0].themeName
              )} = {
  themeName: '${Object.values(albumTheme)[0].themeName}',
  themeImage: '${Object.values(albumTheme)[0].themeImage}',
  size: ${JSON.stringify(Object.values(albumTheme)[0].size)}, 
  color: ${JSON.stringify(Object.values(albumTheme)[0].color)}, 
};

export const  ${camelCase(Object.values(albumTheme)[0].themeName)}Dark = {
  ... ${camelCase(Object.values(albumTheme)[0].themeName)},
  themeName: '${Object.values(albumTheme)[1].themeName}',
  color: ${JSON.stringify(Object.values(albumTheme)[1].color)}, 
};`}
            </pre>
          )}
        </Panel>
        <Panel theme={props.theme}>
          <PanelTitle theme={props.theme}>
            <h3>Preferred Theme</h3>
            <p>
              In the future we will be experimenting with custom theme settings.
            </p>
            <JSONPretty
              className='code'
              data={props.theme.preferredTheme}
              style={{ maxHeight: '250px', overflow: 'auto' }}
            />
          </PanelTitle>
        </Panel>
      </div>
    </SettingsStyles>
  );
};

const Index = () => (
  <ThemeContext.Consumer>
    {(theme) => <Settings theme={theme} />}
  </ThemeContext.Consumer>
);

export default Index;
